import { useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { AdPdfViewer } from './AdPdfViewer';
import { LocationAd } from '@/Locations/types/locations';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import { trackAdView } from '@/lib/analytics';
import { IconCalendar } from '@tabler/icons-react';

interface AdViewerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ad: LocationAd | null;
  locationTitle?: string;
  locationBanner?: string;
}

const formatAdDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), 'MMM d');
  } catch {
    return dateString;
  }
};

const getAdStatus = (startDate: string, endDate: string): 'upcoming' | 'active' | 'expired' => {
  try {
    const now = new Date();
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (isBefore(now, start)) return 'upcoming';
    if (isAfter(now, end)) return 'expired';
    return 'active';
  } catch {
    return 'active';
  }
};

export const AdViewerSheet = ({ open, onOpenChange, ad, locationTitle, locationBanner }: AdViewerSheetProps) => {
  // Track ad view when sheet opens
  useEffect(() => {
    if (open && ad) {
      trackAdView({
        adId: ad.id,
        adTitle: ad.AdTitle,
        adDate: ad.AdStartDate,
      });
    }
  }, [open, ad]);

  if (!ad) return null;

  const status = getAdStatus(ad.AdStartDate, ad.AdEndDate);
  const dateRange = `${formatAdDate(ad.AdStartDate)} - ${formatAdDate(ad.AdEndDate)}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[90vw] sm:max-w-4xl p-0 flex flex-col z-[100]">
        <SheetHeader className="px-6 pt-6 pb-4 border-b space-y-3 text-left">
          <div className="space-y-1">
            <SheetTitle className="text-xl">{locationBanner || ad.AdTitle}{ad.ad_type?.AdType && ` ${ad.ad_type.AdType}`}</SheetTitle>
            {locationTitle && (
              <SheetDescription className="text-sm">{locationTitle}</SheetDescription>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <IconCalendar className="h-4 w-4" />
              <span>Valid: {dateRange}</span>
            </div>
            <Badge
              variant={status === 'active' ? 'default' : status === 'upcoming' ? 'secondary' : 'outline'}
              className={status === 'active' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
            >
              {status === 'active' && 'Active'}
              {status === 'upcoming' && 'Upcoming'}
              {status === 'expired' && 'Ending Soon'}
            </Badge>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-hidden">
          {ad.AdFile?.url && <AdPdfViewer pdfUrl={ad.AdFile.url} />}
        </div>
      </SheetContent>
    </Sheet>
  );
};
